import React, { useEffect, useRef, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { Flex, Box, Text, TextField } from "@radix-ui/themes";
import {
  PersonIcon,
  LockClosedIcon,
  CalendarIcon,
  ClockIcon,
  FileTextIcon,
} from "@radix-ui/react-icons";
import Button from "../../components/common/Button";
import { useAuth } from "../../contexts/AuthContext";
import styles from "./styles.module.scss";

const Login = () => {
  const leftSectionRef = useRef(null);
  const formRef = useRef(null);
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [loginError, setLoginError] = useState("");

  const initialValues = {
    username: "",
    password: "",
  };

  const validationSchema = Yup.object({
    username: Yup.string()
      .email("Please enter a valid email address")
      .required("Email is required"),
    password: Yup.string()
      .required("Password is required")
      .min(6, "Password must be at least 6 characters"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    setLoginError("");
    
    try {
      const result = await login(values.username, values.password);
      
      if (result.success) {
        // Redirect to dashboard on successful login
        navigate("/patient-dashboard");
      } else {
        setLoginError(result.error || "Login failed. Please try again.");
      }
    } catch (error) {
      setLoginError("An unexpected error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/patient-dashboard");
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Simple fade-in animation
    const leftSection = leftSectionRef.current;
    const formSection = formRef.current;

    if (leftSection) {
      leftSection.style.opacity = "0";
      leftSection.style.transform = "translateX(-20px)";
      setTimeout(() => {
        if (!leftSectionRef.current) return;
        leftSection.style.transition = "opacity 0.8s ease, transform 0.8s ease";
        leftSection.style.opacity = "1";
        leftSection.style.transform = "translateX(0)";
      }, 100);
    }

    if (formSection) {
      formSection.style.opacity = "0";
      formSection.style.transform = "translateX(20px)";
      setTimeout(() => {
        if (!formRef.current) return;
        formSection.style.transition = "opacity 0.8s ease, transform 0.8s ease";
        formSection.style.opacity = "1";
        formSection.style.transform = "translateX(0)";
      }, 200);
    }

    return () => {
      // clean-up timers if component unmounts before timeouts fire
      leftSection?.removeAttribute("style");
      formSection?.removeAttribute("style");
    };
  }, []);

  return (
    <div className={styles["login-container"]}>
      <div className={styles["login-wrapper"]}>
        {/* Left Section - Branding & Info */}
        <div ref={leftSectionRef} className={styles["left-section"]}>
          <div className={styles["brand-content"]}>
            <Text size="9" weight="bold" className={styles["brand-title"]}>
              Welcome to Your
              <span className={styles["brand-accent"]}> Renova Journey</span>
            </Text>
            <Text size="4" className={styles["brand-subtitle"]}>
              Your personalized portal to track every step of your
              transformation
            </Text>

            <div className={styles["info-box"]}>
              <Text size="5" className={styles["info-text"]}>
                First time here?
              </Text>
              <Text size="4" className={styles["info-description"]}>
                On your first appointment, you will receive a username and
                password to access this portal. Use this portal to track your
                history and all your Renova journey—including your history and
                upcoming appointments.
              </Text>
            </div>

            <div className={styles["features-grid"]}>
              <div className={styles["feature-card"]}>
                <CalendarIcon className={styles["feature-icon"]} />
                <Text size="3" weight="medium">
                  Appointment History
                </Text>
              </div>
              <div className={styles["feature-card"]}>
                <ClockIcon className={styles["feature-icon"]} />
                <Text size="3" weight="medium">
                  Upcoming Visits
                </Text>
              </div>
              <div className={styles["feature-card"]}>
                <FileTextIcon className={styles["feature-icon"]} />
                <Text size="3" weight="medium">
                  Treatment Records
                </Text>
              </div>
            </div>
          </div>
        </div>

        {/* Right Section - Login Form */}
        <div ref={formRef} className={styles["right-section"]}>
          <Box className={styles["login-box"]}>
            <div className={styles["form-header"]}>
              <Text size="7" weight="bold" className={styles["form-title"]}>
                Sign In
              </Text>
              <br />
              <Text size="3" className={styles["form-subtitle"]}>
                Access your patient portal
              </Text>
            </div>

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                isSubmitting,
              }) => (
                <Form className={styles["login-form"]}>
                  {loginError && (
                    <Box className={styles["error-banner"]}>
                      <Text size="2" color="red" weight="medium">
                        {loginError}
                      </Text>
                    </Box>
                  )}
                  <Flex
                    direction="column"
                    gap="4"
                    className={styles["form-fields"]}
                  >
                    <Box>
                      <TextField.Root
                        size="3"
                        placeholder="Email"
                        type="text"
                        name="username"
                        value={values.username}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={styles.textField}
                        variant={
                          touched.username && errors.username
                            ? "surface"
                            : "soft"
                        }
                        color={
                          touched.username && errors.username
                            ? "red"
                            : undefined
                        }
                        style={{
                          backgroundColor:
                            touched.username && errors.username
                              ? "rgba(255, 0, 0, 0.1)"
                              : undefined,
                        }}
                      >
                        <TextField.Slot>
                          <PersonIcon />
                        </TextField.Slot>
                      </TextField.Root>
                      {touched.username && errors.username && (
                        <Text size="1" color="red" className={styles.errorText}>
                          {errors.username}
                        </Text>
                      )}
                    </Box>

                    <Box>
                      <TextField.Root
                        size="3"
                        placeholder="Password"
                        type="password"
                        name="password"
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={styles.textField}
                        variant={
                          touched.password && errors.password
                            ? "surface"
                            : "soft"
                        }
                        color={
                          touched.password && errors.password
                            ? "red"
                            : undefined
                        }
                        style={{
                          backgroundColor:
                            touched.password && errors.password
                              ? "rgba(255, 0, 0, 0.1)"
                              : undefined,
                        }}
                      >
                        <TextField.Slot>
                          <LockClosedIcon />
                        </TextField.Slot>
                      </TextField.Root>
                      {touched.password && errors.password && (
                        <Text size="1" color="red" className={styles.errorText}>
                          {errors.password}
                        </Text>
                      )}
                    </Box>

                    <Button
                      type="submit"
                      variant="normal"
                      size="3"
                      disabled={isSubmitting}
                      backgroundColor="var(--color-primary)"
                      textColor="var(--color-secondary)"
                      className={styles["login-button"]}
                    >
                      {isSubmitting ? "Logging in..." : "Log In"}
                    </Button>
                  </Flex>
                </Form>
              )}
            </Formik>
          </Box>
        </div>
      </div>
    </div>
  );
};

export default Login;
